// react
import React, { Component } from 'react';

// third-party
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// application
import AsyncAction from './AsyncAction';
import Currency from './Currency';
import ProductGallery from './ProductGallery';
import ProductDescription from './ProductDescription';
import Rating from './Rating';
import { cartAddItem } from '../../store/cart';
import { compareAddItem } from '../../store/compare';
import { Wishlist16Svg, Compare16Svg } from '../../svg';
import { wishlistAddItem } from '../../store/wishlist';
import { toast } from 'react-toastify';
import axios from 'axios';
import { updateAction } from '../../store/auth/authActions';


function dispatchFunc(Component) {
    function dispatchFunc(props) {
        let dispatch = useDispatch();
        return <Component dispatch={dispatch} {...props} />;
    }
    return dispatchFunc;
}

export function runFlyToCartAnimation(startElement, imageUrl) {
    if (typeof window === 'undefined') return;

    const isMobile = window.innerWidth <= 991;

    // 1. Locate the active, visible cart target
    // Mobile cart is in .mobile-header, desktop cart is in .site-header
    const candidateSelectors = isMobile
        ? [
            '.mobile-header .indicator--mobile .indicator__area',
            '.mobile-header .indicator--mobile .indicator__button',
            '.mobile-header .indicator--mobile',
            '.mobile-header a[href*="cart"]',
            '.indicator--mobile .indicator__area',
            '.indicator--mobile',
            '.site-header .indicator--cart .indicator__area',
            '.indicator--cart',
        ]
        : [
            '.site-header .indicator--cart .indicator__area',
            '.site-header .indicator--cart .indicator__button',
            '.site-header .indicator--cart',
            '.indicator--cart .indicator__area',
            '.indicator--cart',
            '.mobile-header .indicator--mobile .indicator__area',
            '.mobile-header .indicator--mobile',
        ];

    let cartTarget = null;
    let targetRect = null;

    for (let i = 0; i < candidateSelectors.length; i++) {
        const el = document.querySelector(candidateSelectors[i]);
        if (el) {
            const r = el.getBoundingClientRect();
            // Ensure element is actually rendered on screen and visible
            if (r.width > 0 && r.height > 0) {
                cartTarget = el;
                targetRect = r;
                break;
            }
        }
    }

    // Target coordinates with fallback to standard header cart position if target is off-screen
    const targetCenterX = targetRect && targetRect.width > 0 && targetRect.left > 0
        ? targetRect.left + targetRect.width / 2
        : (isMobile ? window.innerWidth - 34 : window.innerWidth - 100);

    const targetCenterY = targetRect && targetRect.height > 0 && targetRect.top >= 0 && targetRect.bottom <= window.innerHeight
        ? targetRect.top + targetRect.height / 2
        : (isMobile ? 30 : 37);

    // 2. Locate source element (the clicked button or product image)
    let sourceEl = null;
    if (startElement && startElement.getBoundingClientRect) {
        const sr = startElement.getBoundingClientRect();
        if (sr.width > 0 && sr.height > 0 && sr.top < window.innerHeight && sr.bottom > 0) {
            sourceEl = startElement;
        }
    }

    if (!sourceEl) {
        const imgEl = document.querySelector('.product-image__img');
        if (imgEl) {
            const ir = imgEl.getBoundingClientRect();
            if (ir.width > 0 && ir.height > 0) {
                sourceEl = imgEl;
            }
        }
    }

    if (!sourceEl) {
        sourceEl = startElement;
    }

    const startRect = sourceEl && sourceEl.getBoundingClientRect ? sourceEl.getBoundingClientRect() : null;
    if (!startRect) return;

    // 3. Create flyer particle
    const flyerSize = isMobile ? 40 : 48;
    const flyerRadius = flyerSize / 2;

    const startCenterX = startRect.left + startRect.width / 2;
    const startCenterY = startRect.top + startRect.height / 2;
    const startX = startCenterX - flyerRadius;
    const startY = startCenterY - flyerRadius;

    const deltaX = targetCenterX - startCenterX;
    const deltaY = targetCenterY - startCenterY;

    const flyer = document.createElement('div');
    flyer.className = 'fly-to-cart-particle';
    flyer.style.cssText = `
        position: fixed;
        left: ${startX}px;
        top: ${startY}px;
        width: ${flyerSize}px;
        height: ${flyerSize}px;
        border-radius: 50%;
        background-color: #ffffff;
        background-image: url('${imageUrl || ''}');
        background-size: contain;
        background-position: center;
        background-repeat: no-repeat;
        border: 2px solid #0064d2;
        box-shadow: 0 8px 24px rgba(0, 100, 210, 0.45);
        z-index: 999999;
        pointer-events: none;
        will-change: transform, opacity;
    `;

    document.body.appendChild(flyer);

    // Dynamic Arc: Calculate apex based on vertical and horizontal distance
    const midX = deltaX * 0.4;
    // When flying upward (deltaY < 0), arc slightly higher for a smooth natural toss
    const midY = deltaY * 0.3 - (isMobile ? 35 : 55);

    const keyframes = [
        {
            transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)',
            opacity: 1,
        },
        {
            transform: `translate3d(${midX}px, ${midY}px, 0) scale(1.15) rotate(14deg)`,
            opacity: 0.96,
            offset: 0.38,
        },
        {
            transform: `translate3d(${deltaX * 0.78}px, ${deltaY * 0.76}px, 0) scale(0.65) rotate(-6deg)`,
            opacity: 0.85,
            offset: 0.75,
        },
        {
            transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.18) rotate(-16deg)`,
            opacity: 0.1,
        },
    ];

    if (flyer.animate) {
        const anim = flyer.animate(keyframes, {
            duration: isMobile ? 580 : 650,
            easing: 'cubic-bezier(0.2, 0.8, 0.25, 1)',
            fill: 'forwards',
        });
        anim.onfinish = () => {
            flyer.remove();
            if (cartTarget) {
                cartTarget.classList.remove('cart-bump-animation');
                void cartTarget.offsetWidth;
                cartTarget.classList.add('cart-bump-animation');
                setTimeout(() => {
                    cartTarget.classList.remove('cart-bump-animation');
                }, 600);
            }
        };
    } else {
        flyer.remove();
    }
}

class Product extends Component {
    constructor(props) {
        super(props);

        this.state = {
            quantity: 1,
            loadingAdd: false,
            loadingBuy: false,
            update: false,
            justAdded: false,
        };
    }

    handleChangeQuantity = (quantity) => {
        this.setState({ quantity });
    };



    getActiveToken = () => {
        const { auth: { token } = {} } = this.props;
        if (token) return token;
        try {
            const stored = JSON.parse(localStorage.getItem('state') || '{}');
            const found = stored?.auth?.token || localStorage.getItem('token');
            if (found) {
                localStorage.setItem('token', found);
                return found;
            }
        } catch (e) {
            return localStorage.getItem('token');
        }
        return localStorage.getItem('token');
    };

    add2Cart = async (body) => {
        const activeToken = this.getActiveToken();
        const { history, dispatch, product } = this.props;

        if (!activeToken) {
            toast.warning('Silakan login terlebih dahulu untuk menambah ke keranjang');
            if (history) {
                history.push('/account/login');
            } else {
                window.location.href = '/account/login';
            }
            return;
        }

        try {
            this.setState({ loadingAdd: true });

            // Trigger lightweight, elegant fly-to-cart animation
            runFlyToCartAnimation(this.addToCartBtnRef, product?.image_url || product?.photo);

            await axios({
                method: "POST",
                url: `${process.env.REACT_APP_HOST_API}/cart/save`,
                headers: {
                    Authorization: `Bearer ${activeToken}`,
                },
                data: body,
            });

            this.setState({ loadingAdd: false, justAdded: true, update: true });
            toast.success('Produk berhasil ditambahkan ke keranjang');

            setTimeout(() => {
                this.setState({ justAdded: false, update: false });
            }, 1800);

            // Sync cart total in realtime
            try {
                const res = await axios({
                    method: 'GET',
                    url: `${process.env.REACT_APP_HOST_API}/cart/list`,
                    headers: {
                        Authorization: `Bearer ${activeToken}`,
                    },
                });
                const items = Array.isArray(res.data?.response) ? res.data.response : [];
                const sum = items.reduce((acc, curr) => acc + (parseInt(curr.qty, 10) || 0), 0);
                localStorage.setItem('cart_total', sum);
                window.dispatchEvent(new CustomEvent('cart:count_updated', { detail: sum }));
                window.dispatchEvent(new CustomEvent('cart:updated', { detail: body }));
            } catch (e) {
                // Cart empty fallback
            }

            if (dispatch) {
                dispatch(updateAction(true));
                setTimeout(() => {
                    dispatch(updateAction(false));
                }, 1000);
            }
        } catch (error) {
            this.setState({ loadingAdd: false });
            toast.error(error.response?.data?.message || 'Gagal menambahkan produk ke keranjang');
        }
    };

    buyNow = async (body) => {
        const activeToken = this.getActiveToken();
        const { history, dispatch } = this.props;

        if (!activeToken) {
            toast.warning('Silakan login terlebih dahulu untuk membeli barang');
            if (history) {
                history.push('/account/login');
            } else {
                window.location.href = '/account/login';
            }
            return;
        }

        try {
            this.setState({ loadingBuy: true });

            // 1. Add item to cart
            await axios({
                method: "POST",
                url: `${process.env.REACT_APP_HOST_API}/cart/save`,
                headers: {
                    Authorization: `Bearer ${activeToken}`,
                },
                data: body,
            });

            // 2. Fetch cart list to retrieve newly added cart item id
            const cartRes = await axios({
                method: 'GET',
                url: `${process.env.REACT_APP_HOST_API}/cart/list`,
                headers: {
                    Authorization: `Bearer ${activeToken}`,
                },
            });
            const items = Array.isArray(cartRes.data?.response) ? cartRes.data.response : [];
            const targetItem = items.find((it) => String(it.item_id) === String(body.item_id)) || items[items.length - 1];

            if (!targetItem || !targetItem.id) {
                throw new Error('Gagal memproses item untuk checkout');
            }

            // Sync cart total in realtime
            const sum = items.reduce((acc, curr) => acc + (parseInt(curr.qty, 10) || 0), 0);
            localStorage.setItem('cart_total', sum);
            window.dispatchEvent(new CustomEvent('cart:count_updated', { detail: sum }));

            // 3. Ensure user_code is loaded
            let user_code = localStorage.getItem('user_code');
            if (!user_code) {
                try {
                    const infoRes = await axios({
                        method: 'GET',
                        url: `${process.env.REACT_APP_HOST_API}/auth/info`,
                        headers: {
                            Authorization: `Bearer ${activeToken}`,
                        },
                    });
                    if (infoRes.data?.response?.user_code) {
                        user_code = infoRes.data.response.user_code;
                        localStorage.setItem('user_code', user_code);
                    }
                } catch (e) {
                    console.error('Failed to get user_code:', e);
                }
            }

            // 4. Do checkout to create active Sales Order
            const checkoutRes = await axios({
                method: 'POST',
                url: `${process.env.REACT_APP_HOST_API}/so-commerce/do-checkout`,
                headers: {
                    Authorization: `Bearer ${activeToken}`,
                },
                data: {
                    user_code: user_code,
                    list_cart: [targetItem.id],
                },
            });

            this.setState({ loadingBuy: false });
            localStorage.setItem('CO-INFO', JSON.stringify(checkoutRes.data?.response));
            if (dispatch) {
                dispatch(updateAction(true));
                setTimeout(() => {
                    dispatch(updateAction(false));
                }, 1000);
            }

            toast.success('Melanjutkan ke pembayaran');
            if (history) {
                history.push('/shop/checkout');
            } else {
                window.location.href = '/shop/checkout';
            }
        } catch (error) {
            this.setState({ loadingBuy: false });
            console.error('buyNow error:', error);
            toast.error(error.response?.data?.message || error.message || 'Gagal memproses pembelian');
        }
    };

    handleAddToCartAndRedirect = async (body) => {
        const activeToken = this.getActiveToken();
        const { history, dispatch } = this.props;

        if (!activeToken) {
            toast.warning('Silakan login terlebih dahulu untuk membeli barang');
            if (history) {
                history.push('/account/login');
            } else {
                window.location.href = '/account/login';
            }
            return;
        }

        try {
            this.setState({ loadingAdd: true });
            await axios({
                method: "POST",
                url: `${process.env.REACT_APP_HOST_API}/cart/save`,
                headers: {
                    Authorization: `Bearer ${activeToken}`,
                },
                data: body,
            });

            // Sync cart total in realtime
            try {
                const res = await axios({
                    method: 'GET',
                    url: `${process.env.REACT_APP_HOST_API}/cart/list`,
                    headers: {
                        Authorization: `Bearer ${activeToken}`,
                    },
                });
                const items = Array.isArray(res.data?.response) ? res.data.response : [];
                const sum = items.reduce((acc, curr) => acc + (parseInt(curr.qty, 10) || 0), 0);
                localStorage.setItem('cart_total', sum);
                window.dispatchEvent(new CustomEvent('cart:count_updated', { detail: sum }));
                window.dispatchEvent(new CustomEvent('cart:updated', { detail: body }));
            } catch (e) {
                // Cart empty fallback
            }

            if (dispatch) {
                dispatch(updateAction(true));
                setTimeout(() => {
                    dispatch(updateAction(false));
                }, 1000);
            }

            this.setState({ loadingAdd: false });
            toast.success('Produk berhasil ditambahkan ke keranjang');

            if (history) {
                history.push('/cart');
            } else {
                window.location.href = '/cart';
            }
        } catch (error) {
            this.setState({ loadingAdd: false });
            toast.error(error.response?.data?.message || 'Gagal menambahkan produk ke keranjang');
        }
    };

    formatPriceNumber = (val) => {
        const num = Number(val) || 0;
        return new Intl.NumberFormat('id-ID', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(num);
    };

    render() {
        const {
            product,
            layout,
            auth: { token } = {},
            dispatch
        } = this.props;
        const { quantity } = this.state;

        if (!product) return null;

        const isReady = (product.status || '').toUpperCase() === 'READY';
        const hasStock = product.stok !== undefined && product.stok !== null;
        const priceNum = product.price_6 || product.price || 0;
        const comparePriceNum = Number(product.compareAtPrice) || 0;
        const hasDiscount = comparePriceNum > priceNum;
        const discountPercent = hasDiscount ? Math.round(((comparePriceNum - priceNum) / comparePriceNum) * 100) : 0;

        return (
            <div className={`product product--layout--${layout}`}>
                <div className="product__content">
                    {/* Left Column: Product Gallery */}
                    <div className="product__gallery-col">
                        <ProductGallery layout={layout} images={[product.image_1].filter(Boolean)} />
                    </div>

                    {/* Right Column: Complete Product Details */}
                    <div className="product__info-col">
                        {/* Warehouse Location Badge */}
                        <div className="product__location-wrap">
                            <span className="product-card__location-badge">
                                <svg className="product-card__location-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                                    <circle cx="12" cy="9" r="2.5" />
                                </svg>
                                <span className="product-card__location-name">{product.warehouse_name || 'Hokky Bangunan'}</span>
                            </span>
                        </div>

                        {/* Product Title */}
                        <h1 className="product__name">{product.item_name}</h1>

                        {/* Price & Discount */}
                        <div className="product__price-row">
                            <div className="product__prices-main">
                                <span className="product__price-currency">Rp</span>
                                <span className="product__price-val">{this.formatPriceNumber(priceNum)}</span>
                                {hasDiscount && (
                                    <span className="product__price-old-val">Rp{this.formatPriceNumber(comparePriceNum)}</span>
                                )}
                                {hasDiscount && discountPercent > 0 && (
                                    <span className="product__discount-badge">Hemat {discountPercent}%</span>
                                )}
                            </div>

                            {/* Status & Stock Badges */}
                            <div className="product__status-badges">
                                {isReady ? (
                                    <span className="product-status-pill product-status-pill--ready">
                                        Ready
                                    </span>
                                ) : (
                                    <span className="product-status-pill product-status-pill--inden">{product.status || 'Inden'}</span>
                                )}
                                {hasStock && (
                                    <span className="product-status-pill product-status-pill--stock">
                                        Stok: <strong>{product.stok}</strong>
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Purchase Actions (Quantity Stepper + Add To Cart Button) */}
                        <div className="product__actions-section">
                            <div className="product__qty-group">
                                <label className="product__qty-label">Jumlah</label>
                                <div className="product__qty-stepper">
                                    <button
                                        type="button"
                                        className="product__qty-btn product__qty-btn--sub"
                                        onClick={() => this.handleChangeQuantity(Math.max(1, quantity - 1))}
                                        disabled={quantity <= 1}
                                        aria-label="Kurangi jumlah"
                                    >
                                        <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none">
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                    </button>
                                    <input
                                        type="number"
                                        className="product__qty-input"
                                        value={quantity}
                                        min={1}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value, 10);
                                            this.handleChangeQuantity(Number.isNaN(val) ? 1 : Math.max(1, val));
                                        }}
                                        aria-label="Jumlah produk"
                                    />
                                    <button
                                        type="button"
                                        className="product__qty-btn product__qty-btn--add"
                                        onClick={() => this.handleChangeQuantity(quantity + 1)}
                                        aria-label="Tambah jumlah"
                                    >
                                        <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none">
                                            <line x1="12" y1="5" x2="12" y2="19" />
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="product__btn-group">
                                <button
                                    ref={(el) => { this.addToCartBtnRef = el; }}
                                    type="button"
                                    onClick={() => this.add2Cart({
                                        item_id: product.item_id || product.id,
                                        qty: quantity,
                                        warehouse_id: product.warehouse_id || 1,
                                        price_6: product.price_6 || 0,
                                        type: product.type || 1,
                                    })}
                                    disabled={!quantity || this.state.loadingAdd || this.state.justAdded}
                                    className={classNames('product__add-cart-btn', {
                                        'btn-loading': this.state.loadingAdd,
                                        'product__add-cart-btn--success': this.state.justAdded,
                                    })}
                                >
                                    {this.state.loadingAdd ? (
                                        <i className="fas fa-spinner fa-spin" />
                                    ) : this.state.justAdded ? (
                                        <React.Fragment>
                                            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2.5" fill="none" style={{ marginRight: 6 }}>
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            <span>Berhasil Masuk Keranjang!</span>
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment>
                                            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.2" fill="none" style={{ marginRight: 6, flexShrink: 0 }}>
                                                <circle cx="9" cy="21" r="1" />
                                                <circle cx="20" cy="21" r="1" />
                                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                            </svg>
                                            <span>Tambah ke Keranjang</span>
                                        </React.Fragment>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="product__section-divider" />

                        {/* Formatted Description (Tokopedia Style) */}
                        <div className="product__description-block">
                            <ProductDescription product={product} description={product.item_description} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

Product.propTypes = {
    /** product object */
    product: PropTypes.object.isRequired,
    /** one of ['standard', 'sidebar', 'columnar', 'quickview'] (default: 'standard') */
    layout: PropTypes.oneOf(['standard', 'sidebar', 'columnar', 'quickview']),
};

Product.defaultProps = {
    layout: 'standard',
};

const mapDispatchToProps = {
    cartAddItem,
    wishlistAddItem,
    compareAddItem,
};

const mapStateToProps = (state) => ({ auth: state.auth })

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps,
)(dispatchFunc(Product)));
